import { useDebounce } from '@react-hook/debounce'
import type { AutoCompleteProps, InputProps, InputRef } from 'antd'
import { isEqual } from 'lodash'
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'

export type NullableValue<T = string> = T | null
interface AutoFocusField
  extends Pick<InputProps, 'value' | 'onChange' | 'onFocus' | 'onBlur'> {
  ref: any
}

interface AutoCompleteField
  extends Pick<AutoCompleteProps, 'value' | 'onChange' | 'onBlur' | 'onFocus'> {
  ref: any
}
export interface AutoFocusFieldResult<T> {
  inputField: AutoFocusField
  initialValue: NullableValue<T>
  value: NullableValue<T>
  setValue: Dispatch<SetStateAction<T>>
  error?: string
  setError: Dispatch<SetStateAction<string>>
  isFocused: boolean
  focus: () => void
}

export interface AutoCompleteFieldResult<T>
  extends Omit<AutoFocusFieldResult<T>, 'inputField'> {
  inputField: AutoCompleteField
}

export type AutoFocusedFields<TValue> = {
  [key in keyof TValue]-?: AutoFocusFieldResult<TValue[key]>
}

const useInputState = <T = string>(initialValue: T) => {
  const ref = useRef<InputRef | undefined>(undefined)
  const [isFocused, setFocused] = useState(false)
  const [value, setValue] = useState(initialValue)
  const [error, setError] = useState<string | undefined>()

  useEffect(() => setError(undefined), [value])

  return { ref, setValue, setFocused, value, error, setError, isFocused }
}

export const useField = <T = string | number>(
  initialValue?: T
): AutoFocusFieldResult<T> => {
  const { ref, setValue, setFocused, value, error, setError, isFocused } =
    useInputState<T>(initialValue)

  return {
    inputField: {
      ref,
      onChange: event => setValue(event.target.value as any),
      value: value ? String(value) : '',
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
    },
    initialValue,
    value,
    error,
    setError,
    setValue,
    isFocused,
    focus: () => {
      if (ref.current) {
        ref.current.focus()
      }
    },
  }
}

export const useAutoCompleteField = (
  initialValue: string
): AutoCompleteFieldResult<string> => {
  const { ref, setValue, setFocused, value, error, setError, isFocused } =
    useInputState(initialValue)

  return {
    inputField: {
      ref,
      onChange: value => setValue(value as any),
      value: value ? String(value) : '',
      onFocus: () => undefined,
      onBlur: () => setFocused(false),
    },
    initialValue,
    value,
    error,
    setError,
    setValue,
    isFocused,
    focus: () => {
      if (ref.current) {
        ref.current.focus()
      }
    },
  }
}

export interface AutoSaveFieldResult<T> extends AutoFocusFieldResult<T> {
  isSaving: boolean
}

export const useAutosaveField = <T = string>(
  field: AutoFocusFieldResult<T>,
  handleSave: (value: NullableValue<T>) => Promise<any>
): AutoSaveFieldResult<T> => {
  const { initialValue, value } = field
  const [unsavedValue, setUnsavedValue] = useDebounce(value, 500)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => setUnsavedValue(value), [value])
  useEffect(() => {
    if (isEqual(unsavedValue, initialValue)) {
      return
    }
    setIsSaving(true)
    handleSave(unsavedValue).finally(() => {
      setIsSaving(false)
    })
  }, [unsavedValue])

  return { ...field, isSaving }
}

export function useAutosaveFields<TValue extends {}>(
  fields: AutoFocusedFields<TValue>,
  handleSave: (value: TValue) => Promise<any>
) {
  const allFields = Object.keys(fields).map(name => ({
    name,
    field: fields[name],
  })) as {
    name: keyof TValue
    field: AutoFocusFieldResult<any>
  }[]
  const initialValue = allFields.reduce(
    (all, currentField) => ({
      ...all,
      [currentField.name]: currentField.field.initialValue,
    }),
    {}
  ) as TValue
  const [unsavedValue, setUnsavedValue] = useDebounce(initialValue, 500)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(
    () => {
      const latestValue = allFields.reduce(
        (all, currentField) => ({
          ...all,
          [currentField.name]: currentField.field.value,
        }),
        initialValue
      )
      setUnsavedValue(latestValue)
    },
    allFields.map(({ field }) => field.value)
  )

  useEffect(() => {
    if (isEqual(unsavedValue, initialValue)) {
      return
    }
    setIsSaving(true)
    handleSave(unsavedValue).finally(() => {
      setIsSaving(false)
      const focusedField = allFields.find(({ field }) => field.isFocused)
      if (focusedField) {
        focusedField.field.focus()
      }
    })
  }, [unsavedValue])

  return { fields, isSaving }
}
